import {
  CodeRef,
  Corporate5050GridProps,
  CorporateEventProps,
  CorporateNewsRoomProps,
  CorporateNavigationBlockProps,
  CorporateBillboardProps,
  CorporateBillboardGridProps,
  CorporateArticleProps,
  CorporateFormProps,
  ContactInformation,
  ContactInformationGrid,
} from '@oresundsbron/api/src/types/contentful';

import dynamic from 'next/dynamic';
import { Suspense, lazy } from 'react';

const CorporateNavigationBlock = lazy(
  () => import('./CorporateNavigationBlock')
);
const CorporateBillboard = dynamic(() => import('./CorporateBillboard'));
const CorporateBillboardGrid = dynamic(
  () => import('./CorporateBillboardGrid')
);
const Corporate5050Grid = dynamic(() => import('./Corporate5050Grid'));
const Corporate5050Wrapper = dynamic(() => import('./Corporate5050Wrapper'));
const CorporateArticle = dynamic(() => import('./CorporateArticle'));
const CorporateNewsRoom = dynamic(() => import('./CorporateNewsRoom'));
const CorporateEvent = dynamic(() => import('./CorporateEvent'));
const CorporateForm = dynamic(() => import('./CorporateForm/index'));
const CorporateContact = dynamic(() => import('./CorporateContact'));
const CorporateContactsGrid = dynamic(() => import('./CorporateContactsGrid'));

interface CorporateContentRendererProps {
  content:
    | CorporateNavigationBlockProps
    | CorporateBillboardProps
    | CorporateBillboardGridProps
    | Corporate5050GridProps
    | CorporateNewsRoomProps
    | CorporateEventProps
    | CorporateFormProps
    | ContactInformation
    | ContactInformationGrid
    | CodeRef;
}

export function CorporateContentRenderer({
  content,
}: CorporateContentRendererProps) {
  const renderContent = () => {
    switch (content.__typename) {
      case 'ComponentCorporateNavigationBlock':
        return (
          <div className="mx-4">
            <CorporateNavigationBlock
              content={content as CorporateNavigationBlockProps}
            />
          </div>
        );

      case 'ComponentCorporateBillboard':
        return (
          <CorporateBillboard content={content as CorporateBillboardProps} />
        );

      case 'ComponentCorporateBillboardGrid':
        return (
          <div className="mx-4">
            <CorporateBillboardGrid
              content={content as CorporateBillboardGridProps}
            />
          </div>
        );

      case 'ComponentCorporate5050Grid':
        return (
          <div className="mx-4">
            <Corporate5050Wrapper>
              <Corporate5050Grid content={content as Corporate5050GridProps} />
            </Corporate5050Wrapper>
          </div>
        );

      case 'ComponentCorporateArticle':
        return (
          <div className="mx-auto w-full max-w-7xl">
            <div className="mx-4">
              <CorporateArticle content={content as CorporateArticleProps} />
            </div>
          </div>
        );

      case 'ComponentCorporateNewsRoom':
        return (
          <CorporateNewsRoom content={content as CorporateNewsRoomProps} />
        );

      case 'ComponentCorporateEvent':
        return (
          <div className="mx-4">
            <CorporateEvent content={content as CorporateEventProps} />
          </div>
        );

      case 'ComponentCorporateForm':
        return (
          <div className="mx-4">
            <CorporateForm content={content as CorporateFormProps} />
          </div>
        );

      case 'ComponentContactInformation':
        return <CorporateContact contact={content as ContactInformation} />;

      case 'ComponentCorporateContactsGrid':
        return (
          <CorporateContactsGrid contact={content as ContactInformationGrid} />
        );

      default:
        return <div>Unknown content type: {content.__typename}</div>;
    }
  };

  return (
    <Suspense fallback={<div>Loading...</div>}>{renderContent()}</Suspense>
  );
}

export default CorporateContentRenderer;
